import { ReactNode, useEffect, useRef, useState } from 'react';
import { Box, Skeleton, Stack, SxProps, Typography, useTheme } from '@mui/material';
import useEmblaCarousel from 'embla-carousel-react';
import throttle from 'lodash/throttle';
import { ContainedButton } from '../contained-button';
import { InfoCard } from '../info-card';
import { LandingSectionTitle } from '../landing-section-title';
import { PrevNextBtns } from '../prev-next-btns';
import { coinsIcon, roomIcon, squareMeterIcon } from '../../config/icons';
import { OrderType, TProject } from '../../types/config';
import { useIsMobile } from '../../hooks';
import { calculateFlex, optimisedImage } from '../../utils';

import { CarouselSideArrowBtn, CarouselSideArrowContainer } from './styles';
import { ChevronLeft, ChevronRight, Lucide } from '../../index';

export type ProjectCarouselProps = {
  label?: string;
  projectCardStyles?: SxProps;
  showArrows?: boolean;
  showSideArrows?: boolean;
  showMainButton?: boolean;
  badge3DRule?: OrderType[];
  showPromotionalLabel?: boolean;
  align?: 'start' | 'center' | 'end';
  //////
  projects: TProject[];
  isLoadingCardButton?: (p: TProject) => boolean;
  isDisabledCardButton?: (p: TProject) => boolean;
  getCardButtonLabel?: (p: TProject) => string;
  t: (key: string, options?: any) => string;
  onExplore?: (p: TProject) => void;
};

const InfoItem = ({ icon, text }: { icon: ReactNode; text: string }) => {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {icon}
      <Typography variant="body2" fontWeight="bolder">
        {text}
      </Typography>
    </Stack>
  );
};

export function ProjectsCarousel({
  label,
  projectCardStyles,
  showArrows,
  showSideArrows = false,
  showMainButton,
  badge3DRule = [OrderType.ThreeD],
  showPromotionalLabel = false,
  align = 'start',
  //////
  projects,
  isLoadingCardButton = () => false,
  isDisabledCardButton = () => false,
  getCardButtonLabel = () => t('labels.explore'),
  t,
  onExplore,
}: ProjectCarouselProps) {
  const isMobile = useIsMobile();
  const [isOverflow, setIsOverflow] = useState(false);
  const direction = useTheme().direction;
  const isRTL = direction === 'rtl';

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    active: isOverflow,
    slidesToScroll: 'auto',
    containScroll: 'keepSnaps',
    align,
    direction,
  });

  const handleExplore = (p: TProject) => async () => {
    onExplore && onExplore(p);
  };

  const flexContainerRef = useRef<HTMLDivElement>(null);

  // we want the carousel active only when there are more items than the container width
  useEffect(() => {
    const handleResize = throttle(() => {
      if (!flexContainerRef.current) return;

      // Get the container width
      const containerWidth = flexContainerRef.current.getBoundingClientRect().width;

      // Calculate total width of all children including gaps
      const children = Array.from(flexContainerRef.current.children);
      const totalWidth = children.reduce((acc, child, index) => {
        const childWidth = child.getBoundingClientRect().width;
        // Add gap width
        const gapWidth = index < children.length - 1 ? 16 : 0;
        return acc + childWidth + gapWidth;
      }, 0);

      setIsOverflow(totalWidth > containerWidth);
    }, 500);

    // Initial check after render
    const initialCheck = setTimeout(() => {
      handleResize();
    }, 100);

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(initialCheck);
      handleResize.cancel();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Stack width="100%" spacing={2} position="relative" sx={{ userSelect: 'none' }}>
      {(label || showArrows) && (
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-end"
          justifyContent="space-between"
          sx={{
            width: '100%',
            padding: { xs: '0 16px', sm: '0' },
          }}
        >
          <LandingSectionTitle title={label} />

          {showArrows && isOverflow && (
            <PrevNextBtns
              onPrev={() => emblaApi?.scrollPrev()}
              onNext={() => emblaApi?.scrollNext()}
            />
          )}
        </Stack>
      )}

      {isOverflow && showSideArrows && (
        <>
          <CarouselSideArrowContainer isLeft={isRTL}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              sx={{
                width: '100%',
                height: '100%',
                cursor: 'pointer',
              }}
              onClick={() => emblaApi?.scrollNext()}
            >
              <CarouselSideArrowBtn isLeft={isRTL}>
                <Lucide icon={ChevronLeft} />
              </CarouselSideArrowBtn>
            </Stack>
          </CarouselSideArrowContainer>

          <CarouselSideArrowContainer isLeft={!isRTL}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-start"
              sx={{
                width: '100%',
                height: '100%',
                cursor: 'pointer',
              }}
              onClick={() => emblaApi?.scrollPrev()}
            >
              <CarouselSideArrowBtn isLeft={!isRTL}>
                <Lucide icon={ChevronRight} />
              </CarouselSideArrowBtn>
            </Stack>
          </CarouselSideArrowContainer>
        </>
      )}
      <Box overflow="hidden" ref={emblaRef}>
        {/* Embla container */}
        <Stack
          ref={flexContainerRef}
          direction="row"
          spacing={2}
          px={projects.length > 4 || isMobile ? 2 : 0}
          // Projects carousel on the bottom of the landing page, need to be flex-start when the carousel is not active (isOverflow = false)
          // If they need to be centered somewhere else, a prop will be needed to justifyContent conditionally
          justifyContent="flex-start"
          sx={{ width: '100%' }}
        >
          {projects.map((p) => {
            return (
              <InfoCard
                key={p.id}
                asButton={!showMainButton}
                onClick={!showMainButton ? handleExplore(p) : undefined}
                sx={{
                  flex: {
                    xs: calculateFlex(1.5),
                    sm: calculateFlex(2.5),
                    md: calculateFlex(3.5),
                    lg: calculateFlex(4.5),
                  },
                  height: '100%',
                  ...projectCardStyles,
                }}
                // optimisedImage - needs sync draft functionality
                // imageSrc={optimisedImage(p.mainImageUrl, 'medium')}
                imageSrc={p.mainImageUrl}
                title={p.title}
                description={p.subTitle}
                show3DBadge={badge3DRule.includes(p.orderType)}
                showPromotionalLabel={
                  showPromotionalLabel && p.promotionalLabel ? p.promotionalLabel : undefined
                }
                actions={
                  showMainButton ? (
                    <>
                      <ContainedButton
                        onClick={handleExplore(p)}
                        loading={isLoadingCardButton(p)}
                        disabled={isDisabledCardButton(p)}
                        label={getCardButtonLabel(p)}
                        buttonSx={{ width: '100%' }}
                      />
                    </>
                  ) : undefined
                }
                buildingLabel={p.address}
                infoContent={
                  <>
                    {p.price && (
                      <InfoItem icon={coinsIcon} text={t('labels.fromPrice', { v: p.price })} />
                    )}
                    {p.rooms && (
                      <InfoItem icon={roomIcon} text={t('labels.lowerCaseRooms', { v: p.rooms })} />
                    )}
                    {p.area && (
                      <InfoItem icon={squareMeterIcon} text={t('labels.m2', { v: p.area })} />
                    )}
                    {p.logoUrl ? (
                      <img src={p.logoUrl} style={{ height: '35px' }} />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        width={100}
                        height={35}
                        animation={false}
                        sx={{ borderRadius: 1 }}
                      />
                    )}
                  </>
                }
                infoContentDivider={false}
              />
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
}
